import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card, Button } from '../components';
import { RichTextEditor } from '../components/RichTextEditor';
import { RichTextContent } from '../components/RichTextContent';
import { Heart, MessageSquare, Eye, Send, ArrowLeft, User, Edit, Check, X, CornerDownRight, Trash2 } from 'lucide-react';
import { communityApi, CommunityComment } from '../services/communityApi';
import { useAuthStore } from '../store/authStore';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';

export const CommunityPostDetailPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  const [comment, setComment] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');

  const { data: post, isLoading } = useQuery({
    queryKey: ['community-post', id],
    queryFn: () => communityApi.getPost(id!),
    enabled: !!id,
  });

  const likeMutation = useMutation({
    mutationFn: () => communityApi.toggleLike(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['community-post', id] });
    },
    onError: () => toast.error(t('community.somethingWentWrong')),
  });

  const commentMutation = useMutation({
    mutationFn: (content: string) => communityApi.addComment(id!, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['community-post', id] });
      setComment('');
      toast.success(t('communityPostDetail.commentPlaced'));
    },
    onError: () => toast.error(t('community.somethingWentWrong')),
  });

  const replyMutation = useMutation({
    mutationFn: ({ content, parent_id }: { content: string; parent_id: string }) =>
      communityApi.addComment(id!, content, parent_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['community-post', id] });
      setReplyingTo(null);
      setReplyContent('');
      toast.success(t('communityPostDetail.commentPlaced'));
    },
    onError: () => toast.error(t('community.somethingWentWrong')),
  });

  const deletePostMutation = useMutation({
    mutationFn: () => communityApi.deletePost(id!),
    onSuccess: () => {
      toast.success(t('communityPostDetail.postDeleted'));
      navigate('/community');
    },
    onError: () => toast.error(t('community.somethingWentWrong')),
  });

  const deleteCommentMutation = useMutation({
    mutationFn: (commentId: string) => communityApi.deleteComment(id!, commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['community-post', id] });
      toast.success(t('communityPostDetail.commentDeleted'));
    },
    onError: () => toast.error(t('community.somethingWentWrong')),
  });

  const editMutation = useMutation({
    mutationFn: () =>
      communityApi.updatePost(id!, { title: editTitle, content: editContent }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['community-post', id] });
      setIsEditing(false);
      toast.success(t('communityPostDetail.postUpdated'));
    },
    onError: () => toast.error(t('community.somethingWentWrong')),
  });

  const startEdit = () => {
    if (!post) return;
    setEditTitle(post.title);
    setEditContent(post.content || '');
    setIsEditing(true);
  };

  const isOwnPost = post && user && post.author_id === user.id;

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/2" />
          <div className="h-4 bg-gray-100 rounded w-full" />
          <div className="h-4 bg-gray-100 rounded w-3/4" />
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <p className="text-gray-500 text-center py-8">{t('communityPostDetail.notFound')}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
        <Link to="/community" className="hover:text-[#0B1F3A]">{t('community.title')}</Link>
        <span>›</span>
        <Link to={`/community/kanaal/${post.category_slug}`} className="hover:text-[#0B1F3A]">
          {post.category_professional_type ? t(`screening.types.${post.category_professional_type}`, { defaultValue: post.category_name }) : post.category_name}
        </Link>
        <span>›</span>
        <span className="text-gray-700 truncate max-w-[200px]">{post.title}</span>
      </div>

      {/* Post */}
      <Card className="mb-6">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-[#0B1F3A] flex items-center justify-center text-white font-semibold flex-shrink-0">
              {post.profile_photo_path ? (
                <img src={post.profile_photo_path} alt="" className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <User className="w-5 h-5" />
              )}
            </div>
            <div>
              <div className="font-semibold text-gray-900">
                {post.first_name} {post.last_name}
              </div>
              <div className="text-xs text-gray-400">
                {format(new Date(post.created_at), 'd MMMM yyyy HH:mm', { locale: nl })} in{' '}
                <Link to={`/community/kanaal/${post.category_slug}`} className="text-[#0B1F3A] hover:underline">
                  {post.category_professional_type ? t(`screening.types.${post.category_professional_type}`, { defaultValue: post.category_name }) : post.category_name}
                </Link>
              </div>
            </div>
          </div>
          {!isEditing && (
            <div className="flex items-center gap-2">
              {isOwnPost && (
                <button
                  onClick={startEdit}
                  className="flex items-center gap-1 text-xs text-gray-400 hover:text-[#0B1F3A] transition-colors"
                >
                  <Edit className="w-3.5 h-3.5" />
                  {t('communityPostDetail.edit')}
                </button>
              )}
              {(isOwnPost || isAdmin) && (
                <button
                  onClick={() => {
                    if (window.confirm(t('communityPostDetail.confirmDeletePost'))) {
                      deletePostMutation.mutate();
                    }
                  }}
                  className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-500 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  {t('communityPostDetail.delete')}
                </button>
              )}
            </div>
          )}
        </div>

        {isEditing ? (
          <div className="space-y-3">
            <input
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-base font-bold focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
            />
            <RichTextEditor
              content={editContent}
              onChange={setEditContent}
              minHeight={200}
            />
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditing(false)}
              >
                <X className="w-4 h-4 mr-1" />
                {t('communityPostDetail.cancel')}
              </Button>
              <Button
                size="sm"
                onClick={() => editMutation.mutate()}
                loading={editMutation.isPending}
              >
                <Check className="w-4 h-4 mr-1" />
                {t('communityPostDetail.save')}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <h1 className="text-xl font-bold text-gray-900 mb-4">{post.title}</h1>
            {post.featured_image && (
              <div className="mb-4 rounded-lg overflow-hidden">
                <img
                  src={post.featured_image}
                  alt={post.title}
                  className="w-full h-56 object-cover"
                />
              </div>
            )}
            <RichTextContent html={post.content || ''} />
          </>
        )}

        {/* Actions */}
        {!isEditing && (
          <div className="mt-6 pt-4 border-t border-gray-100 flex items-center gap-4 text-sm text-gray-500">
            <button
              onClick={() => likeMutation.mutate()}
              className={`flex items-center gap-1.5 transition-colors hover:text-red-500 ${post.user_liked ? 'text-red-500' : ''}`}
            >
              <Heart className={`w-4 h-4 ${post.user_liked ? 'fill-current' : ''}`} />
              {post.likes_count} {post.likes_count === 1 ? 'like' : 'likes'}
            </button>
            <span className="flex items-center gap-1.5">
              <MessageSquare className="w-4 h-4" />
              {post.comments_count} {post.comments_count === 1 ? t('communityPostDetail.reactionSingular') : t('communityPostDetail.reactionPlural')}
            </span>
            <span className="flex items-center gap-1.5">
              <Eye className="w-4 h-4" />
              {post.views_count} {t('communityPostDetail.views')}
            </span>
          </div>
        )}
      </Card>

      {/* Comments */}
      {post.comments && post.comments.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">
            {post.comments.length} {post.comments.length === 1 ? t('communityPostDetail.reactionSingular') : t('communityPostDetail.reactionPlural')}
          </h2>
          <div className="space-y-3">
            {post.comments.map((c: CommunityComment) => (
              <Card key={c.id} className="!p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                    {c.profile_photo_path ? (
                      <img src={c.profile_photo_path} alt="" className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <User className="w-4 h-4 text-gray-500" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm text-gray-900">
                        {c.first_name} {c.last_name}
                      </span>
                      <span className="text-xs text-gray-400">
                        {format(new Date(c.created_at), 'd MMM yyyy HH:mm', { locale: nl })}
                      </span>
                    </div>
                    <RichTextContent html={c.content} className="text-sm" />
                    <div className="mt-2 flex items-center gap-3">
                      <button
                        onClick={() => {
                          setReplyingTo(replyingTo === c.id ? null : c.id);
                          setReplyContent('');
                        }}
                        className="flex items-center gap-1 text-xs text-gray-400 hover:text-[#0B1F3A] transition-colors"
                      >
                        <CornerDownRight className="w-3 h-3" />
                        {t('communityPostDetail.reply')}
                      </button>
                      {(isAdmin || (user && c.author_id === user.id)) && (
                        <button
                          onClick={() => {
                            if (window.confirm(t('communityPostDetail.confirmDeleteComment'))) {
                              deleteCommentMutation.mutate(c.id);
                            }
                          }}
                          className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="w-3 h-3" />
                          {t('communityPostDetail.delete')}
                        </button>
                      )}
                    </div>

                    {/* Inline reply editor */}
                    {replyingTo === c.id && (
                      <div className="mt-3 pl-3 border-l-2 border-[#0B1F3A]/20">
                        <RichTextEditor
                          content={replyContent}
                          onChange={setReplyContent}
                          placeholder={t('communityPostDetail.writeReplyPlaceholder')}
                          minHeight={80}
                          simple
                        />
                        <div className="mt-2 flex gap-2 justify-end">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setReplyingTo(null); setReplyContent(''); }}
                          >
                            <X className="w-3.5 h-3.5 mr-1" />
                            {t('communityPostDetail.cancel')}
                          </Button>
                          <Button
                            size="sm"
                            loading={replyMutation.isPending}
                            disabled={!replyContent || replyContent === '<p></p>'}
                            onClick={() => {
                              const plain = replyContent.replace(/<[^>]+>/g, '').trim();
                              if (plain.length < 2) { toast.error(t('communityPostDetail.replyTooShort')); return; }
                              replyMutation.mutate({ content: replyContent, parent_id: c.id });
                            }}
                          >
                            <Send className="w-3.5 h-3.5 mr-1" />
                            {t('communityPostDetail.reply')}
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Nested replies */}
                    {c.replies && c.replies.length > 0 && (
                      <div className="mt-3 pl-3 border-l-2 border-gray-200 space-y-3">
                        {c.replies.map((r: CommunityComment) => (
                          <div key={r.id} className="flex items-start gap-2">
                            <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                              {r.profile_photo_path ? (
                                <img src={r.profile_photo_path} alt="" className="w-7 h-7 rounded-full object-cover" />
                              ) : (
                                <User className="w-3.5 h-3.5 text-gray-400" />
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-xs text-gray-900">
                                  {r.first_name} {r.last_name}
                                </span>
                                <span className="text-xs text-gray-400">
                                  {format(new Date(r.created_at), 'd MMM yyyy HH:mm', { locale: nl })}
                                </span>
                              </div>
                              <RichTextContent html={r.content} className="text-sm" />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Add comment with mini editor */}
      <Card>
        <h3 className="font-semibold text-gray-900 mb-3">{t('communityPostDetail.writeComment')}</h3>
        <RichTextEditor
          content={comment}
          onChange={setComment}
          placeholder={t('communityPostDetail.writeCommentPlaceholder')}
          minHeight={100}
          simple
        />
        <div className="mt-3 flex justify-between items-center">
          <Link
            to={`/community/kanaal/${post.category_slug}`}
            className="text-sm text-[#0B1F3A] hover:underline flex items-center gap-1"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            {t('communityPostDetail.backToChannel', { name: post.category_professional_type ? t(`screening.types.${post.category_professional_type}`, { defaultValue: post.category_name }) : post.category_name })}
          </Link>
          <Button
            onClick={() => {
              const plain = comment.replace(/<[^>]+>/g, '').trim();
              if (plain.length < 2) {
                toast.error(t('communityPostDetail.replyTooShort'));
                return;
              }
              commentMutation.mutate(comment);
            }}
            loading={commentMutation.isPending}
            disabled={!comment || comment === '<p></p>'}
          >
            <Send className="w-4 h-4 mr-2" />
            {t('communityPostDetail.reply')}
          </Button>
        </div>
      </Card>
    </div>
  );
};
